/**
 * Frontend API Client for Mietwerkstatt Backend
 * Vollständige TypeScript API-Client-Klasse
 *
 * Version: 1.0.0
 * Generated: August 2025
 */

import {
  // Response Types
  ApiResponse,
  PaginatedResponse,
  // Entity Types
  User,
  Customer,
  Company,
  RentalObject,
  RentalObjectBundle,
  PriceStructure,
  Article,
  ArticleCategoryInfo,
  ArticleUnitInfo,
  Rental,
  RentalItem,
  Invoice,
  OpeningHours,
  Holiday,
  BusinessStatus,
  BusinessStatusMode,
  ApiKey,
  // Request Types
  LoginRequest,
  LoginResponse,
  RefreshTokenResponse,
  RefreshTokenRequest,
  ChangePasswordRequest,
  UpdateProfileRequest,
  RegisterRequest,
  CreateUserRequest,
  UpdateUserRequest,
  ResetUserPasswordRequest,
  CreateCustomerRequest,
  UpdateCustomerRequest,
  CreateCompanyRequest,
  UpdateCompanyRequest,
  CreateRentalObjectRequest,
  UpdateRentalObjectRequest,
  CreatePriceStructureRequest,
  UpdatePriceStructureRequest,
  CreateArticleRequest,
  UpdateArticleRequest,
  CreateRentalRequest,
  UpdateRentalRequest,
  CreateInvoiceRequest,
  UpdateInvoiceStatusRequest,
  SetBusinessStatusRequest,
  SetOpeningHoursRequest,
  CreateHolidayRequest,
  CreateApiKeyRequest,
  QuickActionRequest,
  // Filter Types
  UserFilters,
  CustomerFilters,
  CompanyFilters,
  RentalObjectFilters,
  PriceStructureFilters,
  ArticleFilters,
  RentalFilters,
  InvoiceFilters,
  // Statistics Types
  UserStats,
  CustomerStats,
  CompanyStats,
  RentalObjectStats,
  ArticleStats,
  BusinessStatusInfo,
  // Other Types
  PriceCalculation,
  LivePriceUpdate,
  VatNumberValidation,
  IbanValidation,
  AvailabilityCheck,
  BundleCatalogEntry,
  AddAdditionalArticlesRequest,
  SetAdditionalArticlesRequest,
  PaginationParams,
  SearchParams,
  AutocompleteResult,
  // Revenue Analytics Types
  RevenueFilters,
  RevenueStats,
  RevenueExportData,
  RevenueOverview,
  RevenueTrends,
  // 3CX Integration Types
  ThreeCXStatus,
  ThreeCXSyncResult,
  ThreeCXComparison,
  ThreeCXForceUpdateResult,
  ThreeCXSyncAnalysis,
  // Phone Offset Types
  PhoneOffsets,
  SetPhoneOffsetsRequest,
  SetPhoneOffsetsResponse,
  // Legacy Import Types
  LegacyInvoiceRow,
  LegacyInvoiceGroup,
  LegacyImportResult,
  LegacyImportValidation,
  LegacyImportFileRequest,
  LegacyImportTextRequest,
  // System Types
  SystemInfo,
  SystemActionResult,
  BackupResult,
  LogEntry,
  SystemHealth,
  SystemStats,
  AiSettings,
  LiftOccupancyDryRunResult,
  PresenceCameraConfig,
  PresenceCameraVerdict,
  PresenceObjectCheckResult,
  ChatReservation,
  ChatVisit,
  ReservationPolicy,
  CreateChatReservationRequest,
  CreateChatVisitRequest,
  ObjectType,
} from "./api-types";

export class MietwerkstattApiClient {
  private baseUrl: string;
  private accessToken?: string;
  private storedRefreshToken?: string;

  constructor(baseUrl: string = "http://localhost:3001/api") {
    this.baseUrl = baseUrl;
  }

  // ============================================
  // AUTHENTICATION METHODS
  // ============================================

  /**
   * Set access token for authenticated requests
   */
  setAccessToken(token: string): void {
    console.log(
      "🔐 Setting access token in API client:",
      token ? "Token provided" : "Empty token"
    );
    this.accessToken = token;
    console.log(
      "🔍 Current access token state:",
      this.accessToken ? "Token set" : "No token"
    );
  }

  /**
   * Set refresh token for token renewal
   */
  setRefreshToken(token: string): void {
    console.log(
      "🔐 Setting refresh token in API client:",
      token ? "Token provided" : "Empty token"
    );
    this.storedRefreshToken = token;
    console.log(
      "🔍 Current refresh token state:",
      this.storedRefreshToken ? "Token set" : "No token"
    );
  }

  /**
   * Clear access token
   */
  clearToken(): void {
    console.log("🗑️ Clearing access and refresh tokens from API client");
    this.accessToken = undefined;
    this.storedRefreshToken = undefined;
  }

  /**
   * Login user
   */
  async login(request: LoginRequest): Promise<LoginResponse> {
    const response = await this.post<any>("/auth/login", request);
    // Backend sendet: { message, user, tokens } direkt - response ist das rohe Backend-Response
    const loginResponse: LoginResponse = {
      user: (response as any).user,
      tokens: (response as any).tokens,
    };

    if (loginResponse.tokens?.accessToken) {
      this.setAccessToken(loginResponse.tokens.accessToken);
    }
    if (loginResponse.tokens?.refreshToken) {
      this.setRefreshToken(loginResponse.tokens.refreshToken);
    }
    return loginResponse;
  }

  /**
   * Refresh access token
   */
  async refreshToken(
    request: RefreshTokenRequest
  ): Promise<RefreshTokenResponse> {
    const response = await this.post<any>("/auth/refresh", request);
    const tokens = (response as any).tokens || response.data?.tokens;

    if (!tokens?.accessToken) {
      throw new Error("Ungültiges Token-Refresh-Format vom Server");
    }

    if (tokens.accessToken) {
      this.setAccessToken(tokens.accessToken);
    }
    if (tokens.refreshToken) {
      this.setRefreshToken(tokens.refreshToken);
    }

    return { tokens };
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    await this.post("/auth/logout");
    this.clearToken();
  }

  /**
   * Get current user info
   */
  async getCurrentUser(): Promise<User> {
    const response = await this.get<any>("/auth/me");
    const user = (response as any).user || response.data?.user;

    if (!user) {
      throw new Error("Ungültiges Benutzer-Response-Format vom Server");
    }

    return user;
  }

  /**
   * Update current user's profile
   */
  async updateCurrentUser(request: UpdateProfileRequest): Promise<User> {
    const response = await this.put<any>("/auth/me", request);
    const user = (response as any).user || response.data;

    if (!user) {
      throw new Error("Ungültiges Profil-Response-Format vom Server");
    }

    return user;
  }

  /**
   * Change password
   */
  async changePassword(request: ChangePasswordRequest): Promise<void> {
    await this.post("/auth/change-password", request);
  }

  /**
   * Register new user (admin only)
   */
  async register(request: RegisterRequest): Promise<User> {
    const response = await this.post<User>("/auth/register", request);
    return response.data!;
  }

  // ============================================
  // USER METHODS
  // ============================================

  /**
   * Get all users
   */
  async getUsers(filters?: UserFilters): Promise<User[]> {
    const response = await this.get<User[]>("/users", filters);
    return response.data!;
  }

  /**
   * Search users
   */
  async searchUsers(params: SearchParams): Promise<AutocompleteResult<User>> {
    const response = await this.get<User[]>("/users/search", params);
    return { items: response.data!, total: response.count || 0 };
  }

  /**
   * Get users by role
   */
  async getUsersByRole(role: string): Promise<User[]> {
    const response = await this.get<User[]>(`/users/role/${role}`);
    return response.data!;
  }

  /**
   * Get user statistics
   */
  async getUserStats(): Promise<UserStats> {
    const response = await this.get<UserStats>("/users/stats");
    return response.data!;
  }

  /**
   * Get user by ID
   */
  async getUser(id: string): Promise<User> {
    const response = await this.get<User>(`/users/${id}`);
    return response.data!;
  }

  /**
   * Create new user
   */
  async createUser(request: CreateUserRequest): Promise<User> {
    const response = await this.post<User>("/users", request);
    return response.data!;
  }

  /**
   * Update user
   */
  async updateUser(id: string, request: UpdateUserRequest): Promise<User> {
    const response = await this.put<User>(`/users/${id}`, request);
    return response.data!;
  }

  /**
   * Delete user
   */
  async deleteUser(id: string): Promise<void> {
    await this.delete(`/users/${id}`);
  }

  /**
   * Toggle user status
   */
  async toggleUserStatus(id: string): Promise<User> {
    const response = await this.post<User>(`/users/${id}/toggle-status`);
    return response.data!;
  }

  /**
   * Reset user password
   */
  async resetUserPassword(
    id: string,
    request: ResetUserPasswordRequest
  ): Promise<void> {
    await this.post(`/users/${id}/reset-password`, request);
  }

  // ============================================
  // CUSTOMER METHODS
  // ============================================

  /**
   * Get all customers
   */
  async getCustomers(filters?: CustomerFilters): Promise<Customer[]> {
    const response = await this.get<Customer[]>("/customers", filters);
    return response.data!;
  }

  /**
   * Search customers
   */
  async searchCustomers(
    params: SearchParams
  ): Promise<AutocompleteResult<Customer>> {
    const response = await this.get<Customer[]>("/customers/search", params);
    return { items: response.data!, total: response.count || 0 };
  }

  /**
   * Get customer statistics
   */
  async getCustomerStats(): Promise<CustomerStats> {
    const response = await this.get<CustomerStats>("/customers/stats");
    return response.data!;
  }

  /**
   * Get customers with expiring licenses
   */
  async getCustomersWithExpiringLicenses(
    days: number = 30
  ): Promise<Customer[]> {
    const response = await this.get<Customer[]>(
      "/customers/expiring-licenses",
      { days }
    );
    return response.data!;
  }

  /**
   * Get customer by email
   */
  async getCustomerByEmail(email: string): Promise<Customer> {
    const response = await this.get<Customer>(`/customers/email/${email}`);
    return response.data!;
  }

  /**
   * Get customer by ID
   */
  async getCustomer(id: string): Promise<Customer> {
    const response = await this.get<Customer>(`/customers/${id}`);
    return response.data!;
  }

  /**
   * Create new customer
   */
  async createCustomer(request: CreateCustomerRequest): Promise<Customer> {
    const response = await this.post<Customer>("/customers", request);
    return response.data!;
  }

  /**
   * Update customer
   */
  async updateCustomer(
    id: string,
    request: UpdateCustomerRequest
  ): Promise<Customer> {
    const response = await this.put<Customer>(`/customers/${id}`, request);
    return response.data!;
  }

  /**
   * Delete customer
   */
  async deleteCustomer(id: string): Promise<void> {
    await this.delete(`/customers/${id}`);
  }

  /**
   * Permanently delete customer
   */
  async permanentDeleteCustomer(id: string): Promise<void> {
    await this.delete(`/customers/${id}/permanent`);
  }

  /**
   * Toggle customer status
   */
  async toggleCustomerStatus(id: string): Promise<Customer> {
    const response = await this.post<Customer>(
      `/customers/${id}/toggle-status`
    );
    return response.data!;
  }

  // ============================================
  // COMPANY METHODS
  // ============================================

  /**
   * Get all companies
   */
  async getCompanies(filters?: CompanyFilters): Promise<Company[]> {
    const response = await this.get<Company[]>("/companies", filters);
    return response.data!;
  }

  /**
   * Get primary company
   */
  async getPrimaryCompany(): Promise<Company> {
    const response = await this.get<Company>("/companies/primary");
    return response.data!;
  }

  /**
   * Get company statistics
   */
  async getCompanyStats(): Promise<CompanyStats> {
    const response = await this.get<CompanyStats>("/companies/stats");
    return response.data!;
  }

  /**
   * Get company by ID
   */
  async getCompany(id: string): Promise<Company> {
    const response = await this.get<Company>(`/companies/${id}`);
    return response.data!;
  }

  /**
   * Create new company
   */
  async createCompany(request: CreateCompanyRequest): Promise<Company> {
    const response = await this.post<Company>("/companies", request);
    return response.data!;
  }

  /**
   * Update company
   */
  async updateCompany(
    id: string,
    request: UpdateCompanyRequest
  ): Promise<Company> {
    const response = await this.put<Company>(`/companies/${id}`, request);
    return response.data!;
  }

  /**
   * Set primary company
   */
  async setPrimaryCompany(id: string): Promise<Company> {
    const response = await this.post<Company>(`/companies/${id}/set-primary`);
    return response.data!;
  }

  /**
   * Update company logo
   */
  async updateCompanyLogo(id: string, logoPath: string): Promise<Company> {
    const response = await this.put<Company>(`/companies/${id}/logo`, {
      logoPath,
    });
    return response.data!;
  }

  /**
   * Delete company
   */
  async deleteCompany(id: string): Promise<void> {
    await this.delete(`/companies/${id}`);
  }

  /**
   * Permanently delete company
   */
  async permanentDeleteCompany(id: string): Promise<void> {
    await this.delete(`/companies/${id}/permanent`);
  }

  /**
   * Toggle company status
   */
  async toggleCompanyStatus(id: string): Promise<Company> {
    const response = await this.post<Company>(`/companies/${id}/toggle-status`);
    return response.data!;
  }

  /**
   * Get reservation policy and current reservable capacity
   */
  async getReservationPolicy(): Promise<ReservationPolicy> {
    const response = await this.get<ReservationPolicy>(
      "/companies/reservations/policy"
    );
    return response.data!;
  }

  /**
   * Get chat reservations
   */
  async getChatReservations(filters?: {
    date?: string;
    objectType?: ObjectType.HEBEBUEHNE | ObjectType.ARBEITSPLATZ;
  }): Promise<ChatReservation[]> {
    const response = await this.get<ChatReservation[]>(
      "/companies/reservations",
      filters
    );
    return response.data!;
  }

  /**
   * Create chat reservation
   */
  async createChatReservation(
    request: CreateChatReservationRequest
  ): Promise<ChatReservation> {
    const response = await this.post<ChatReservation>(
      "/companies/reservations",
      request
    );
    return response.data!;
  }

  /**
   * Cancel chat reservation
   */
  async cancelChatReservation(id: string): Promise<ChatReservation> {
    const response = await this.post<ChatReservation>(
      `/companies/reservations/${id}/cancel`
    );
    return response.data!;
  }

  async getChatVisits(filters?: { date?: string }): Promise<ChatVisit[]> {
    const response = await this.get<ChatVisit[]>(
      "/companies/visit-calendar",
      filters
    );
    return response.data!;
  }

  async createChatVisit(request: CreateChatVisitRequest): Promise<ChatVisit> {
    const response = await this.post<ChatVisit>(
      "/companies/visit-calendar",
      request
    );
    return response.data!;
  }

  async cancelChatVisit(id: string): Promise<ChatVisit> {
    const response = await this.post<ChatVisit>(
      `/companies/visit-calendar/${id}/cancel`
    );
    return response.data!;
  }

  /**
   * Validate VAT number
   */
  async validateVatNumber(vatNumber: string): Promise<VatNumberValidation> {
    const response = await this.post<VatNumberValidation>(
      "/companies/validate/vat-number",
      { vatNumber }
    );
    return response.data!;
  }

  /**
   * Validate IBAN
   */
  async validateIban(iban: string): Promise<IbanValidation> {
    const response = await this.post<IbanValidation>(
      "/companies/validate/iban",
      { iban }
    );
    return response.data!;
  }

  // ============================================
  // RENTAL OBJECT METHODS
  // ============================================

  /**
   * Get all rental objects
   */
  async getRentalObjects(
    filters?: RentalObjectFilters
  ): Promise<RentalObject[]> {
    const response = await this.get<RentalObject[]>("/rental-objects", filters);
    return response.data!;
  }

  /**
   * Get rental objects by type
   */
  async getRentalObjectsByType(
    type: string,
    activeOnly: boolean = true
  ): Promise<RentalObject[]> {
    const response = await this.get<RentalObject[]>(
      `/rental-objects/types/${type}`,
      { activeOnly }
    );
    return response.data!;
  }

  /**
   * Get rental object statistics
   */
  async getRentalObjectStats(): Promise<RentalObjectStats> {
    const response = await this.get<RentalObjectStats>("/rental-objects/stats");
    return response.data!;
  }

  /**
   * Get available rental objects
   */
  async getAvailableRentalObjects(type?: string): Promise<RentalObject[]> {
    const response = await this.get<RentalObject[]>(
      "/rental-objects/available",
      type ? { type } : undefined
    );
    return response.data!;
  }

  /**
   * Get rental object by ID
   */
  async getRentalObject(
    id: string,
    includeRelations: boolean = false
  ): Promise<RentalObject> {
    const response = await this.get<RentalObject>(`/rental-objects/${id}`, {
      includeRelations,
    });
    return response.data!;
  }

  /**
   * Check rental object availability
   */
  async checkRentalObjectAvailability(id: string): Promise<AvailabilityCheck> {
    const response = await this.get<AvailabilityCheck>(
      `/rental-objects/${id}/availability`
    );
    return response.data!;
  }

  /**
   * Create new rental object
   */
  async createRentalObject(
    request: CreateRentalObjectRequest
  ): Promise<RentalObject> {
    const response = await this.post<RentalObject>("/rental-objects", request);
    return response.data!;
  }

  /**
   * Update rental object
   */
  async updateRentalObject(
    id: string,
    request: UpdateRentalObjectRequest
  ): Promise<RentalObject> {
    const response = await this.put<RentalObject>(
      `/rental-objects/${id}`,
      request
    );
    return response.data!;
  }

  /**
   * Delete rental object
   */
  async deleteRentalObject(id: string): Promise<void> {
    await this.delete(`/rental-objects/${id}`);
  }

  /**
   * Permanently delete rental object
   */
  async permanentDeleteRentalObject(id: string): Promise<void> {
    await this.delete(`/rental-objects/${id}/permanent`);
  }

  /**
   * Toggle rental object status
   */
  async toggleRentalObjectStatus(id: string): Promise<RentalObject> {
    const response = await this.post<RentalObject>(
      `/rental-objects/${id}/toggle-status`
    );
    return response.data!;
  }

  /**
   * Toggle rental object public status
   */
  async toggleRentalObjectPublic(id: string): Promise<RentalObject> {
    const response = await this.post<RentalObject>(
      `/rental-objects/${id}/toggle-public`
    );
    return response.data!;
  }

  async setRentalObjectInternalBlocker(
    id: string,
    reason = "Manuell belegt",
    source = "manual"
  ): Promise<RentalObject> {
    const response = await this.post<RentalObject>(
      `/rental-objects/${id}/internal-blocker`,
      { reason, source }
    );
    return response.data!;
  }

  async clearRentalObjectInternalBlocker(id: string): Promise<RentalObject> {
    const response = await this.delete<RentalObject>(
      `/rental-objects/${id}/internal-blocker`
    );
    return response.data!;
  }

  /**
   * Assign price structure to rental object
   */
  async assignPriceStructureToObject(
    objectId: string,
    priceStructureId: string
  ): Promise<void> {
    await this.post(
      `/rental-objects/${objectId}/price-structures/${priceStructureId}`
    );
  }

  /**
   * Remove price structure from rental object
   */
  async removePriceStructureFromObject(
    objectId: string,
    priceStructureId: string
  ): Promise<void> {
    await this.delete(
      `/rental-objects/${objectId}/price-structures/${priceStructureId}`
    );
  }

  /**
   * Add additional articles to rental object
   */
  async addAdditionalArticlesToObject(
    objectId: string,
    articleIds: string[]
  ): Promise<RentalObject> {
    const response = await this.post<RentalObject>(
      `/rental-objects/${objectId}/additional-articles`,
      { articleIds }
    );
    return response.data!;
  }

  /**
   * Remove additional articles from rental object
   */
  async removeAdditionalArticlesFromObject(
    objectId: string,
    articleIds: string[]
  ): Promise<RentalObject> {
    const response = await this.request<RentalObject>(
      "DELETE",
      `/rental-objects/${objectId}/additional-articles`,
      { articleIds }
    );
    return response.data!;
  }

  /**
   * Set additional articles for rental object (overwrites existing)
   */
  async setAdditionalArticlesForObject(
    objectId: string,
    articleIds: string[]
  ): Promise<RentalObject> {
    const response = await this.put<RentalObject>(
      `/rental-objects/${objectId}/additional-articles`,
      { articleIds }
    );
    return response.data!;
  }

  /**
   * Get additional articles for rental object
   */
  async getAdditionalArticlesForObject(objectId: string): Promise<Article[]> {
    const response = await this.get<Article[]>(
      `/rental-objects/${objectId}/additional-articles`
    );
    return response.data!;
  }

  /**
   * Get bundle articles for rental object
   */
  async getBundleArticlesForObject(
    objectId: string
  ): Promise<RentalObjectBundle[]> {
    const response = await this.get<RentalObjectBundle[]>(
      `/rental-objects/${objectId}/bundle-articles`
    );
    return response.data!;
  }

  /**
   * Add bundle article to rental object
   */
  async addBundleArticleToObject(
    objectId: string,
    bundleData: {
      articleId: string;
      quantity?: number;
      bundlePrice: number;
      isPublic?: boolean;
      notes?: string;
    }
  ): Promise<RentalObjectBundle> {
    const response = await this.post<RentalObjectBundle>(
      `/rental-objects/${objectId}/bundle-articles`,
      {
        articleId: bundleData.articleId,
        quantity: bundleData.quantity || 1,
        bundlePrice: bundleData.bundlePrice,
        isPublic: bundleData.isPublic || false,
        notes: bundleData.notes,
      }
    );
    return response.data!;
  }

  /**
   * Add bundle article to multiple rental objects
   */
  async addBundleArticleToMultipleObjects(
    objectIds: string[],
    bundleData: {
      articleId: string;
      quantity?: number;
      bundlePrice: number;
      isPublic?: boolean;
      notes?: string;
    }
  ): Promise<RentalObjectBundle[]> {
    const response = await this.post<RentalObjectBundle[]>(
      `/rental-objects/bulk/bundle-articles`,
      {
        objectIds,
        articleId: bundleData.articleId,
        quantity: bundleData.quantity || 1,
        bundlePrice: bundleData.bundlePrice,
        isPublic: bundleData.isPublic || false,
        notes: bundleData.notes,
      }
    );
    return response.data!;
  }

  /**
   * Update bundle article
   */
  async updateBundleArticle(
    objectId: string,
    bundleId: string,
    bundleData: {
      bundlePrice?: number;
      isActive?: boolean;
      isPublic?: boolean;
      notes?: string | null;
    }
  ): Promise<RentalObjectBundle> {
    const response = await this.put<RentalObjectBundle>(
      `/rental-objects/${objectId}/bundle-articles/${bundleId}`,
      bundleData
    );
    return response.data!;
  }

  /**
   * Remove bundle article from rental object
   */
  async removeBundleArticleFromObject(
    objectId: string,
    bundleId: string
  ): Promise<void> {
    await this.delete(
      `/rental-objects/${objectId}/bundle-articles/${bundleId}`
    );
  }

  /**
   * Remove multiple bundle articles (bulk delete)
   */
  async removeBundleArticlesFromObjects(bundleIds: string[]): Promise<{
    deletedCount: number;
    results: Array<{ bundleId: string; success: boolean }>;
    errors: Array<{ bundleId: string; error: string }>;
  }> {
    const response = await this.request<{
      deletedCount: number;
      results: Array<{ bundleId: string; success: boolean }>;
      errors: Array<{ bundleId: string; error: string }>;
    }>("DELETE", "/rental-objects/bulk/bundle-articles", { bundleIds });
    return response.data!;
  }

  // ============================================
  // PRICE STRUCTURE METHODS
  // ============================================

  /**
   * Get all price structures
   */
  async getPriceStructures(
    filters?: PriceStructureFilters
  ): Promise<PriceStructure[]> {
    const response = await this.get<PriceStructure[]>(
      "/pricing/price-structures",
      filters
    );
    return response.data!;
  }

  /**
   * Get price structure by ID
   */
  async getPriceStructure(id: string): Promise<PriceStructure> {
    const response = await this.get<PriceStructure>(
      `/pricing/price-structures/${id}`
    );
    return response.data!;
  }

  /**
   * Create new price structure
   */
  async createPriceStructure(
    request: CreatePriceStructureRequest
  ): Promise<PriceStructure> {
    const response = await this.post<PriceStructure>(
      "/pricing/price-structures",
      request
    );
    return response.data!;
  }

  /**
   * Update price structure
   */
  async updatePriceStructure(
    id: string,
    request: UpdatePriceStructureRequest
  ): Promise<PriceStructure> {
    const response = await this.put<PriceStructure>(
      `/pricing/price-structures/${id}`,
      request
    );
    return response.data!;
  }

  /**
   * Delete price structure
   */
  async deletePriceStructure(id: string): Promise<void> {
    await this.delete(`/pricing/price-structures/${id}`);
  }

  /**
   * Get all global prices
   */
  async getGlobalPrices(): Promise<PriceStructure[]> {
    const response = await this.get<PriceStructure[]>("/pricing/global");
    return response.data!;
  }

  /**
   * Set global price for object type
   */
  async setGlobalPrice(
    objectType: string,
    priceData: any
  ): Promise<PriceStructure> {
    const response = await this.put<PriceStructure>(
      `/pricing/global/${objectType}`,
      priceData
    );
    return response.data!;
  }

  /**
   * Calculate price for object
   */
  async calculateObjectPrice(
    objectId: string,
    hours: number = 1
  ): Promise<PriceCalculation> {
    const response = await this.get<PriceCalculation>(
      `/pricing/calculate/${objectId}`,
      { hours }
    );
    return response.data!;
  }

  /**
   * Set default articles for object
   */
  async setDefaultArticlesForObject(
    objectId: string,
    articleIds: string[]
  ): Promise<void> {
    await this.put(`/pricing/objects/${objectId}/default-articles`, {
      articleIds,
    });
  }

  /**
   * Get default articles for object
   */
  async getDefaultArticlesForObject(objectId: string): Promise<Article[]> {
    const response = await this.get<Article[]>(
      `/pricing/objects/${objectId}/default-articles`
    );
    return response.data!;
  }

  /**
   * Get available articles for default assignment
   */
  async getAvailableArticles(): Promise<Article[]> {
    const response = await this.get<Article[]>("/pricing/articles/available");
    return response.data!;
  }

  /**
   * Get bundle catalog with all rental objects and their additional articles
   */
  async getBundleCatalog(): Promise<BundleCatalogEntry[]> {
    const response = await this.get<BundleCatalogEntry[]>(
      "/pricing/bundle-catalog"
    );
    return response.data!;
  }

  // ============================================
  // ARTICLE METHODS
  // ============================================

  /**
   * Get all articles
   */
  async getArticles(filters?: ArticleFilters): Promise<Article[]> {
    const response = await this.get<Article[]>("/articles", filters);
    console.log("🔍 getArticles raw response:", response);

    // Backend kann Daten direkt zurückgeben oder in response.data
    if (Array.isArray(response)) {
      console.log("✅ Response is direct array");
      return response;
    } else if (response.data && Array.isArray(response.data)) {
      console.log("✅ Response data is array");
      return response.data;
    } else if (
      response &&
      typeof response === "object" &&
      !Array.isArray(response)
    ) {
      // Möglicherweise ist es ein Objekt mit direkten Artikeln
      console.log(
        "🔍 Response is object, checking properties:",
        Object.keys(response)
      );
      return [];
    } else {
      console.log("⚠️ Unexpected response format:", typeof response);
      return [];
    }
  }

  async getArticleCategories(): Promise<ArticleCategoryInfo[]> {
    const response = await this.get<ArticleCategoryInfo[]>(
      "/articles/categories"
    );
    return (response.data || response) as any as ArticleCategoryInfo[];
  }

  async createArticleCategory(request: {
    key?: string;
    label: string;
    sortOrder?: number;
  }): Promise<ArticleCategoryInfo> {
    const response = await this.post<ArticleCategoryInfo>(
      "/articles/categories",
      request
    );
    return (response.data || response) as any as ArticleCategoryInfo;
  }

  async deleteArticleCategory(key: string): Promise<void> {
    await this.delete(`/articles/categories/${encodeURIComponent(key)}`);
  }

  async getArticleUnits(): Promise<ArticleUnitInfo[]> {
    const response = await this.get<ArticleUnitInfo[]>("/articles/units");
    return (response.data || response) as any as ArticleUnitInfo[];
  }

  async createArticleUnit(request: {
    key?: string;
    label: string;
    sortOrder?: number;
  }): Promise<ArticleUnitInfo> {
    const response = await this.post<ArticleUnitInfo>(
      "/articles/units",
      request
    );
    return (response.data || response) as any as ArticleUnitInfo;
  }

  async deleteArticleUnit(key: string): Promise<void> {
    await this.delete(`/articles/units/${encodeURIComponent(key)}`);
  }

  /**
   * Search articles
   */
  async searchArticles(
    params: SearchParams
  ): Promise<AutocompleteResult<Article>> {
    const response = await this.get<Article[]>("/articles/search", params);
    return { items: response.data!, total: response.count || 0 };
  }

  /**
   * Get article statistics
   */
  async getArticleStats(): Promise<ArticleStats> {
    const response = await this.get<ArticleStats>("/articles/stats");
    return response.data!;
  }

  /**
   * Get articles by category
   */
  async getArticlesByCategory(category: string): Promise<Article[]> {
    const response = await this.get<Article[]>(
      `/articles/categories/${category}`
    );
    return response.data!;
  }

  /**
   * Get article by SKU
   */
  async getArticleBySku(sku: string): Promise<Article> {
    const response = await this.get<Article>(`/articles/sku/${sku}`);
    return response.data!;
  }

  /**
   * Get article by ID
   */
  async getArticle(id: string): Promise<Article> {
    const response = await this.get<Article>(`/articles/${id}`);
    return response.data!;
  }

  /**
   * Create new article
   */
  async createArticle(request: CreateArticleRequest): Promise<Article> {
    const response = await this.post<Article>("/articles", request);
    // Backend gibt direkt das Article-Objekt zurück, nicht in einem ApiResponse-Wrapper
    return response as any as Article;
  }

  /**
   * Create multiple articles
   */
  async createArticlesBulk(
    requests: CreateArticleRequest[]
  ): Promise<Article[]> {
    const response = await this.post<Article[]>("/articles/bulk", {
      articles: requests,
    });
    return response.data!;
  }

  /**
   * Update article
   */
  async updateArticle(
    id: string,
    request: UpdateArticleRequest
  ): Promise<Article> {
    const response = await this.put<Article>(`/articles/${id}`, request);
    return (response.data || response) as any as Article;
  }

  /**
   * Delete article
   */
  async deleteArticle(id: string): Promise<void> {
    await this.delete(`/articles/${id}`);
  }

  // ============================================
  // RENTAL METHODS
  // ============================================

  /**
   * Get all rentals
   */
  async getRentals(filters?: RentalFilters): Promise<Rental[]> {
    const response = await this.get<Rental[]>("/rentals", filters);
    return response.data!;
  }

  /**
   * Get rental by ID
   */
  async getRental(id: string): Promise<Rental> {
    // HINWEIS: Das Backend gibt für diesen Endpunkt das Mietobjekt direkt zurück,
    // nicht in ein `data`-Objekt verpackt wie bei den meisten anderen Aufrufen.
    // Daher wird der Rückgabetyp hier explizit umgewandelt.
    const response = await this.get<any>(`/rentals/${id}`);
    return response as unknown as Rental;
  }

  /**
   * Create new rental
   */
  async createRental(request: CreateRentalRequest): Promise<Rental> {
    const response = await this.post<Rental>("/rentals", request);
    return response.data!;
  }

  /**
   * Quick start rental for objects like Hebebühnen
   */
  async quickStartRental(
    objectId: string,
    additionalArticleIds: string[] = [],
    bundleId?: string
  ): Promise<any> {
    const requestData: any = {
      objectId,
      additionalArticleIds,
    };

    if (bundleId) {
      requestData.bundleId = bundleId;
    }

    const response = await this.post<any>("/rentals/quick-start", requestData);
    // Backend returns: { success: true, data: rental, message: "..." }
    return response.data || response;
  }

  /**
   * Update rental
   */
  async updateRental(
    id: string,
    request: UpdateRentalRequest
  ): Promise<Rental> {
    const response = await this.put<Rental>(`/rentals/${id}`, request);
    return response.data!;
  }

  /**
   * End rental
   */
  async endRental(id: string): Promise<Rental> {
    const response = await this.post<Rental>(`/rentals/${id}/end`);
    return response.data!;
  }

  /**
   * End rental with automatic invoice generation and extended response
   * Returns detailed response with rental data, invoice and download URLs
   */
  async endRentalWithInvoice(id: string, endDate: Date): Promise<any> {
    const response = await this.post(`/rentals/${id}/end`, {
      endDate: endDate.toISOString(),
    });
    return response;
  }

  /**
   * Cancel rental
   */
  async cancelRental(id: string): Promise<Rental> {
    const response = await this.post<Rental>(`/rentals/${id}/cancel`);
    return response.data!;
  }

  /**
   * Get live price for rental
   * @param id Rental ID
   * @param endDate Optional preview end date for price calculation
   */
  async getRentalLivePrice(
    id: string,
    endDate?: Date
  ): Promise<LivePriceUpdate> {
    const queryParams = endDate ? `?endDate=${endDate.toISOString()}` : "";
    const response = await this.get<LivePriceUpdate>(
      `/rentals/${id}/live-price${queryParams}`
    );

    // Das Backend gibt direkt die LivePriceUpdate-Daten zurück, nicht in einem data-Wrapper
    return response as any as LivePriceUpdate;
  }

  /**
   * Perform quick action on rental (add articles or end rental)
   */
  async performRentalQuickAction(
    id: string,
    action: QuickActionRequest
  ): Promise<any> {
    const response = await this.post<any>(
      `/rentals/${id}/quick-actions`,
      action
    );
    // Backend returns: { success: true, message: "..." } or error
    return response.data || response;
  }

  /**
   * Add articles to existing rental
   */
  async addArticlesToRental(
    rentalId: string,
    articleIds: string[],
    articles?: Array<{
      articleId: string;
      quantity?: number;
      startedAt?: Date;
      endedAt?: Date;
    }>
  ): Promise<any> {
    return this.performRentalQuickAction(rentalId, {
      action: "addArticles",
      articleIds,
      ...(articles ? { articles } : {}),
    });
  }

  /**
   * End rental via quick action with enhanced response
   */
  async endRentalQuick(rentalId: string, endDate?: Date): Promise<any> {
    const actionData: any = {
      action: "endRental",
    };

    if (endDate) {
      actionData.endDate = endDate.toISOString();
    }

    // Die Quick Action sollte auch die erweiterte Response zurückgeben
    return this.performRentalQuickAction(rentalId, actionData);
  }

  /**
   * Check object availability for rental
   */
  async checkRentalAvailability(objectId: string): Promise<AvailabilityCheck> {
    const response = await this.get<AvailabilityCheck>(
      `/rentals/${objectId}/availability`
    );
    return response.data!;
  }

  /**
   * Get available bundles for a rental object
   */
  async getRentalObjectBundles(
    objectId: string
  ): Promise<RentalObjectBundle[]> {
    const response = await this.get<RentalObjectBundle[]>(
      `/rental-objects/${objectId}/bundles`
    );
    return response.data!;
  }

  /**
   * Add bundle to rental
   */
  async addBundleToRental(rentalId: string, bundleId: string): Promise<Rental> {
    const response = await this.put<Rental>(`/rentals/${rentalId}/bundle`, {
      bundleId,
    });
    return response.data!;
  }

  /**
   * Remove bundle from rental
   */
  async removeBundleFromRental(rentalId: string): Promise<Rental> {
    const response = await this.delete<Rental>(`/rentals/${rentalId}/bundle`);
    return response.data!;
  }

  /**
   * Assign customer to rental
   */
  async assignCustomerToRental(
    rentalId: string,
    customerId: string
  ): Promise<Rental> {
    const response = await this.post<Rental>(
      `/rentals/${rentalId}/assign-customer`,
      { customerId }
    );
    return response.data!;
  }

  /**
   * Add item to rental
   */
  async addItemToRental(
    rentalId: string,
    itemData: {
      articleId: string;
      quantity: number;
      startedAt?: Date;
      endedAt?: Date;
    }
  ): Promise<RentalItem> {
    const response = await this.post<RentalItem>(
      `/rentals/${rentalId}/items`,
      itemData
    );
    return response.data!;
  }

  /**
   * Update rental item
   */
  async updateRentalItem(
    rentalId: string,
    itemId: string,
    updateData: { quantity?: number; startedAt?: Date; endedAt?: Date }
  ): Promise<RentalItem> {
    const response = await this.put<RentalItem>(
      `/rentals/${rentalId}/items/${itemId}`,
      updateData
    );
    return response.data!;
  }

  /**
   * Remove item from rental
   */
  async removeItemFromRental(rentalId: string, itemId: string): Promise<void> {
    await this.delete(`/rentals/${rentalId}/items/${itemId}`);
  }

  // ============================================
  // INVOICE METHODS
  // ============================================

  /**
   * Get all invoices
   */
  async getInvoices(filters?: InvoiceFilters): Promise<Invoice[]> {
    const response = await this.get<Invoice[]>("/invoices", filters);
    return response.data!;
  }

  /**
   * Get invoice by ID
   */
  async getInvoice(id: string): Promise<Invoice> {
    const response = await this.get<Invoice>(`/invoices/${id}`);
    return response.data!;
  }

  /**
   * Create invoice for rental
   */
  async createInvoice(request: CreateInvoiceRequest): Promise<Invoice> {
    const response = await this.post<Invoice>("/invoices", request);
    return response.data!;
  }

  /**
   * Update existing invoice
   */
  async updateInvoice(
    invoiceId: string,
    request: Partial<CreateInvoiceRequest>
  ): Promise<Invoice> {
    const response = await this.put<Invoice>(`/invoices/${invoiceId}`, request);
    return response.data!;
  }

  /**
   * Generate PDF for invoice
   */
  async generateInvoicePdf(invoiceId: string): Promise<{ pdfPath: string }> {
    const response = await this.post<{ pdfPath: string }>(
      `/invoices/${invoiceId}/generate-pdf`
    );
    return response.data!;
  }

  /**
   * Download invoice PDF
   */
  async downloadInvoicePdf(invoiceId: string): Promise<Blob> {
    const response = await fetch(
      `${this.baseUrl}/invoices/${invoiceId}/download`,
      {
        headers: this.getHeaders(),
      }
    );
    return response.blob();
  }

  /**
   * Get invoice by QR code (public)
   */
  async getInvoiceByQrCode(qrCode: string): Promise<Invoice> {
    const response = await this.get<Invoice>(`/invoices/public/${qrCode}`);
    return response.data!;
  }

  /**
   * Download invoice PDF by QR code (public)
   */
  async downloadInvoicePdfByQrCode(qrCode: string): Promise<Blob> {
    const response = await fetch(
      `${this.baseUrl}/invoices/public/${qrCode}/download`
    );
    return response.blob();
  }

  /**
   * Get invoice preview by QR code (public)
   */
  async getInvoicePreviewByQrCode(qrCode: string): Promise<any> {
    const response = await this.get<any>(`/invoices/public/${qrCode}/preview`);
    return response.data!;
  }

  /**
   * Update invoice status
   */
  async updateInvoiceStatus(
    invoiceId: string,
    request: UpdateInvoiceStatusRequest
  ): Promise<Invoice> {
    const response = await this.patch<Invoice>(
      `/invoices/${invoiceId}/status`,
      request
    );
    return response.data!;
  }

  /**
   * Delete invoice
   */
  async deleteInvoice(invoiceId: string): Promise<void> {
    await this.delete(`/invoices/${invoiceId}`);
  }

  // ============================================
  // BUSINESS METHODS
  // ============================================

  /**
   * Get business status
   */
  async getBusinessStatus(): Promise<BusinessStatusInfo> {
    const response = await this.get<BusinessStatusInfo>("/business/status");
    return response.data!;
  }

  /**
   * Set business status
   */
  async setBusinessStatus(
    request: SetBusinessStatusRequest
  ): Promise<BusinessStatus> {
    const response = await this.post<BusinessStatus>(
      "/business/status",
      request
    );
    return response.data!;
  }

  /**
   * Get opening hours
   */
  async getOpeningHours(): Promise<OpeningHours[]> {
    const response = await this.get<OpeningHours[]>("/business/opening-hours");
    return response.data!;
  }

  /**
   * Set opening hours
   */
  async setOpeningHours(
    request: SetOpeningHoursRequest
  ): Promise<OpeningHours> {
    const response = await this.post<OpeningHours>(
      "/business/opening-hours",
      request
    );
    return response.data!;
  }

  /**
   * Set all opening hours at once (Bulk Update with 3CX sync)
   */
  async setAllOpeningHours(
    openingHours: Array<{
      dayOfWeek: string;
      openTime: string | null;
      closeTime: string | null;
      isClosed: boolean;
    }>
  ): Promise<{
    openingHours: OpeningHours[];
    threeCXSync: { success: boolean; error?: string };
    totalUpdated: number;
  }> {
    const response = await this.post<{
      openingHours: OpeningHours[];
      threeCXSync: { success: boolean; error?: string };
      totalUpdated: number;
    }>("/business/opening-hours/bulk", { openingHours });
    return response.data!;
  }

  /**
   * Add holiday
   */
  async addHoliday(request: CreateHolidayRequest): Promise<Holiday> {
    const response = await this.post<Holiday>("/business/holidays", request);
    return response.data!;
  }

  /**
   * Set single day as holiday (for spontaneous closures)
   */
  async setSingleDayHoliday(
    name: string,
    date: Date,
    description?: string
  ): Promise<{
    holiday: Holiday;
    threeCXSync: { success: boolean; error?: string };
  }> {
    const response = await this.post<{
      holiday: Holiday;
      threeCXSync: { success: boolean; error?: string };
    }>("/business/holidays/single-day", {
      name,
      date: date.toISOString(),
      description,
    });
    return response.data!;
  }

  /**
   * Remove single day holiday
   */
  async removeSingleDayHoliday(holidayId: string): Promise<{
    threeCXSync: { success: boolean; error?: string };
  }> {
    const response = await this.delete<{
      threeCXSync: { success: boolean; error?: string };
    }>(`/business/holidays/single-day/${holidayId}`);
    return response.data!;
  }

  /**
   * Force update German holidays
   */
  async forceUpdateGermanHolidays(): Promise<{
    success: boolean;
    totalFetched: number;
    newHolidays: number;
    existingHolidays: number;
    errors: string[];
    summary: string;
  }> {
    const response = await this.post<{
      success: boolean;
      totalFetched: number;
      newHolidays: number;
      existingHolidays: number;
      errors: string[];
      summary: string;
    }>("/business/holidays/force-update-german");
    return response.data!;
  }

  /**
   * Sync German holidays automatically
   */
  async syncGermanHolidays(): Promise<{
    database: {
      imported: number;
      skipped: number;
      updated: number;
      errors: string[];
    };
    threeCX: {
      success: boolean;
      error?: string;
    };
    summary: {
      totalProcessed: number;
      changesApplied: number;
      threeCXSynced: boolean;
    };
  }> {
    const response = await this.post<{
      database: {
        imported: number;
        skipped: number;
        updated: number;
        errors: string[];
      };
      threeCX: {
        success: boolean;
        error?: string;
      };
      summary: {
        totalProcessed: number;
        changesApplied: number;
        threeCXSynced: boolean;
      };
    }>("/business/holidays/sync-german");
    return response.data!;
  }

  /**
   * Preview German holidays from API (without saving)
   */
  async previewGermanHolidays(years?: number[]): Promise<{
    holidays: Array<{
      date: string;
      fname: string;
      comment: string;
    }>;
    count: number;
    years: number[];
  }> {
    const yearParam = years ? `?years=${years.join(",")}` : "";
    const response = await this.get<{
      holidays: Array<{
        date: string;
        fname: string;
        comment: string;
      }>;
      count: number;
      years: number[];
    }>(`/business/holidays/german-preview${yearParam}`);
    return response.data!;
  }

  /**
   * Deactivate all imported holidays
   */
  async deactivateImportedHolidays(): Promise<{
    deactivatedCount: number;
  }> {
    const response = await this.post<{
      deactivatedCount: number;
    }>("/business/holidays/deactivate-imported");
    return response.data!;
  }

  /**
   * Get available holiday years
   */
  async getHolidayYears(): Promise<{
    years: number[];
    count: number;
  }> {
    const response = await this.get<{
      years: number[];
      count: number;
    }>("/business/holidays/years");
    return response.data!;
  }

  /**
   * Get all API keys
   */
  async getApiKeys(): Promise<ApiKey[]> {
    const response = await this.get<ApiKey[]>("/business/api-keys");
    return response.data!;
  }

  /**
   * Create API key
   */
  async createApiKey(request: CreateApiKeyRequest): Promise<ApiKey> {
    const response = await this.post<ApiKey>("/business/api-keys", request);
    return response.data!;
  }

  /**
   * Deactivate API key
   */
  async deactivateApiKey(keyId: string): Promise<void> {
    await this.delete(`/business/api-keys/${keyId}`);
  }

  // ============================================
  // PUBLIC METHODS (NO AUTH REQUIRED)
  // ============================================

  /**
   * Get public business status (requires API key)
   */
  async getPublicBusinessStatus(apiKey: string): Promise<BusinessStatusInfo> {
    const response = await this.get<BusinessStatusInfo>(
      "/public/status",
      undefined,
      { "X-API-Key": apiKey }
    );
    return response.data!;
  }

  /**
   * Get public opening hours (requires API key)
   */
  async getPublicOpeningHours(apiKey: string): Promise<OpeningHours[]> {
    const response = await this.get<OpeningHours[]>(
      "/public/opening-hours",
      undefined,
      { "X-API-Key": apiKey }
    );
    return response.data!;
  }

  /**
   * Get public catalog (requires API key)
   */
  async getPublicCatalog(apiKey: string): Promise<Article[]> {
    const response = await this.get<Article[]>("/public/catalog", undefined, {
      "X-API-Key": apiKey,
    });
    return response.data!;
  }

  /**
   * Get public catalog categories (requires API key)
   */
  async getPublicCatalogCategories(apiKey: string): Promise<string[]> {
    const response = await this.get<string[]>(
      "/public/catalog/categories",
      undefined,
      { "X-API-Key": apiKey }
    );
    return response.data!;
  }

  /**
   * Get public article (requires API key)
   */
  async getPublicArticle(articleId: string, apiKey: string): Promise<Article> {
    const response = await this.get<Article>(
      `/public/catalog/${articleId}`,
      undefined,
      { "X-API-Key": apiKey }
    );
    return response.data!;
  }

  /**
   * Get public rental objects (requires API key)
   */
  async getPublicRentalObjects(apiKey: string): Promise<RentalObject[]> {
    const response = await this.get<RentalObject[]>(
      "/public/objects",
      undefined,
      { "X-API-Key": apiKey }
    );
    return response.data!;
  }

  /**
   * Get public price structures (requires API key)
   */
  async getPublicPriceStructures(apiKey: string): Promise<PriceStructure[]> {
    const response = await this.get<PriceStructure[]>(
      "/public/prices",
      undefined,
      { "X-API-Key": apiKey }
    );
    return response.data!;
  }

  // ============================================
  // HEALTH & UTILITY METHODS
  // ============================================

  /**
   * Health check
   */
  async healthCheck(): Promise<any> {
    const response = await fetch(`${this.baseUrl.replace("/api", "")}/health`);
    return response.json();
  }

  /**
   * Get API overview
   */
  async getApiOverview(): Promise<any> {
    const response = await this.get<any>("/");
    return response.data!;
  }

  // ============================================
  // LEGACY INVOICE IMPORT METHODS
  // ============================================

  /**
   * Import legacy invoices from CSV file
   */
  async importLegacyInvoicesFromFile(file: File): Promise<LegacyImportResult> {
    const formData = new FormData();
    formData.append("csvFile", file);

    const response = await fetch(`${this.baseUrl}/legacy/import`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${this.accessToken}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Import failed");
    }

    const result = await response.json();
    return result.data || result;
  }

  /**
   * Import legacy invoices from CSV text
   */
  async importLegacyInvoicesFromText(
    csvContent: string
  ): Promise<LegacyImportResult> {
    const response = await this.post<LegacyImportResult>(
      "/legacy/import-text",
      {
        csvContent,
      }
    );
    return response.data!;
  }

  /**
   * Validate CSV file format before import
   */
  async validateLegacyCsvFile(file: File): Promise<LegacyImportValidation> {
    const formData = new FormData();
    formData.append("csvFile", file);

    const response = await fetch(`${this.baseUrl}/legacy/validate`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${this.accessToken}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Validation failed");
    }

    const result = await response.json();
    return result.validation || result.data;
  }

  /**
   * Validate CSV text format before import
   */
  async validateLegacyCsvText(
    csvContent: string
  ): Promise<LegacyImportValidation> {
    const response = await this.post<{ validation: LegacyImportValidation }>(
      "/legacy/validate-text",
      { csvContent }
    );
    return response.data!.validation;
  }

  // ============================================
  // SYSTEM METHODS
  // ============================================

  /**
   * Get comprehensive system information
   */
  async getSystemInfo(): Promise<SystemInfo> {
    const response = await this.get<SystemInfo>("/system/info");
    return response.data!;
  }

  /**
   * Create database backup
   */
  async createSystemBackup(): Promise<BackupResult> {
    const response = await this.post<BackupResult>("/system/backup");
    return response.data!;
  }

  /**
   * Download backup file
   */
  async downloadBackup(filename: string): Promise<void> {
    const response = await fetch(
      `${this.baseUrl}/system/backup/download/${filename}`,
      {
        method: "GET",
        headers: this.getHeaders(),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Download failed");
    }

    // Create download link
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Clear system cache
   */
  async clearSystemCache(): Promise<SystemActionResult> {
    const response = await this.post<SystemActionResult>("/system/clear-cache");
    return response.data!;
  }

  /**
   * Get system logs
   */
  async getSystemLogs(limit?: number): Promise<LogEntry[]> {
    const params = limit ? { limit: limit.toString() } : undefined;
    const response = await this.get<LogEntry[]>("/system/logs", params);
    return response.data!;
  }

  /**
   * Prepare system for restart
   */
  async prepareSystemRestart(): Promise<SystemActionResult> {
    const response = await this.post<SystemActionResult>(
      "/system/prepare-restart"
    );
    return response.data!;
  }

  /**
   * Get detailed system health status
   */
  async getSystemHealth(): Promise<{
    systemInfo: SystemInfo;
    health: SystemHealth;
  }> {
    const response = await this.get<{
      systemInfo: SystemInfo;
      health: SystemHealth;
    }>("/system/health");
    return response.data!;
  }

  /**
   * Get system statistics
   */
  async getSystemStats(): Promise<SystemStats> {
    const response = await this.get<SystemStats>("/system/stats");
    return response.data!;
  }

  // ============================================
  // PRIVATE HELPER METHODS
  // ============================================

  private getHeaders(
    additionalHeaders?: Record<string, string>
  ): Record<string, string> {
    // Stelle sicher, dass das Token gesetzt ist
    if (!this.accessToken) {
      const savedToken = localStorage.getItem("accessToken");
      if (savedToken) {
        console.log(
          "🔄 Token not set in client but found in localStorage - setting now"
        );
        this.accessToken = savedToken;
      }
    }

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...additionalHeaders,
    };

    if (this.accessToken) {
      headers.Authorization = `Bearer ${this.accessToken}`;
      console.log("🔑 Authorization header added to request");
    } else {
      console.warn("⚠️ No access token available for request");
    }

    return headers;
  }

  private async request<T>(
    method: string,
    endpoint: string,
    data?: any,
    additionalHeaders?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    let url = `${this.baseUrl}${endpoint}`;
    const headers = this.getHeaders(additionalHeaders);

    console.log(`📡 API Request: ${method} ${url}`);
    console.log("📋 Headers:", {
      ...headers,
      Authorization: headers.Authorization ? "Bearer ***" : undefined,
    });

    const config: RequestInit = {
      method,
      headers,
    };

    if (data && method !== "GET") {
      config.body = JSON.stringify(data);
      console.log("📤 Request body:", data);
    } else if (data && method === "GET") {
      const params = new URLSearchParams();
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
        console.log("🔍 Query params:", queryString);
      }
    }

    try {
      const response = await fetch(url, config);
      const responseText = await response.text();
      let result: any = {};

      if (responseText) {
        try {
          result = JSON.parse(responseText);
        } catch {
          result = { message: responseText };
        }
      }

      console.log(`📥 Response status: ${response.status}`);

      if (!response.ok) {
        console.error("❌ API Error:", result);
        throw new Error(result.message || result.error || "API request failed");
      }

      console.log("✅ API Success:", result);
      return result;
    } catch (error) {
      console.error("💥 Request failed:", error);
      throw error;
    }
  }

  private async get<T>(
    endpoint: string,
    params?: any,
    additionalHeaders?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>("GET", endpoint, params, additionalHeaders);
  }

  private async post<T>(
    endpoint: string,
    data?: any,
    additionalHeaders?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>("POST", endpoint, data, additionalHeaders);
  }

  private async put<T>(
    endpoint: string,
    data?: any,
    additionalHeaders?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>("PUT", endpoint, data, additionalHeaders);
  }

  private async patch<T>(
    endpoint: string,
    data?: any,
    additionalHeaders?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>("PATCH", endpoint, data, additionalHeaders);
  }

  private async delete<T>(
    endpoint: string,
    additionalHeaders?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>("DELETE", endpoint, undefined, additionalHeaders);
  }

  // ============================================
  // REVENUE ANALYTICS METHODS
  // ============================================

  /**
   * Get revenue analytics
   */
  async getRevenueAnalytics(filters?: RevenueFilters): Promise<RevenueStats> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          if (Array.isArray(value)) {
            value.forEach((v) => params.append(key, v.toString()));
          } else {
            params.append(key, value.toString());
          }
        }
      });
    }

    const response = await this.get<RevenueStats>(
      `/revenue/analytics?${params.toString()}`
    );
    return response.data!;
  }

  /**
   * Get revenue export data
   */
  async getRevenueExportData(
    filters?: RevenueFilters
  ): Promise<RevenueExportData> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          if (Array.isArray(value)) {
            value.forEach((v) => params.append(key, v.toString()));
          } else {
            params.append(key, value.toString());
          }
        }
      });
    }

    const response = await this.get<RevenueExportData>(
      `/revenue/export?${params.toString()}`
    );
    return response.data!;
  }

  /**
   * Get revenue overview
   */
  async getRevenueOverview(): Promise<RevenueOverview> {
    const response = await this.get<RevenueOverview>("/revenue/overview");
    return response.data!;
  }

  /**
   * Get revenue trends
   */
  async getRevenueTrends(
    startDate: string,
    endDate: string,
    groupBy?: string
  ): Promise<RevenueTrends> {
    const params = new URLSearchParams({
      startDate,
      endDate,
      ...(groupBy && { groupBy }),
    });

    const response = await this.get<RevenueTrends>(
      `/revenue/trends?${params.toString()}`
    );
    return response.data!;
  }

  // ============================================
  // 3CX INTEGRATION METHODS
  // ============================================

  /**
   * Get 3CX status
   */
  async get3CXStatus(): Promise<ThreeCXStatus> {
    const response = await this.get<ThreeCXStatus>("/business/3cx/status");
    return response.data!;
  }

  /**
   * Full 3CX synchronization
   */
  async sync3CX(companyId?: string): Promise<ThreeCXSyncResult> {
    const response = await this.post<ThreeCXSyncResult>("/business/3cx/sync", {
      companyId,
    });
    return response.data!;
  }

  /**
   * Sync opening hours to 3CX
   */
  async sync3CXOpeningHours(companyId?: string): Promise<void> {
    await this.post("/business/3cx/sync/opening-hours", { companyId });
  }

  /**
   * Sync holidays to 3CX
   */
  async sync3CXHolidays(companyId?: string): Promise<void> {
    await this.post("/business/3cx/sync/holidays", { companyId });
  }

  /**
   * Analyze 3CX sync status
   */
  async analyze3CXSync(companyId?: string): Promise<ThreeCXSyncAnalysis> {
    const params = companyId ? `?companyId=${companyId}` : "";
    const response = await this.get<ThreeCXSyncAnalysis>(
      `/business/3cx/analyze${params}`
    );
    return response.data!;
  }

  /**
   * Compare opening hours with 3CX
   */
  async compare3CXOpeningHours(companyId?: string): Promise<ThreeCXComparison> {
    const params = companyId ? `?companyId=${companyId}` : "";
    const response = await this.get<ThreeCXComparison>(
      `/business/3cx/compare/opening-hours${params}`
    );
    return response.data!;
  }

  /**
   * Compare holidays with 3CX
   */
  async compare3CXHolidays(companyId?: string): Promise<ThreeCXComparison> {
    const params = companyId ? `?companyId=${companyId}` : "";
    const response = await this.get<ThreeCXComparison>(
      `/business/3cx/compare/holidays${params}`
    );
    return response.data!;
  }

  /**
   * Force update 3CX opening hours (Admin only)
   */
  async forceUpdate3CXOpeningHours(
    companyId?: string
  ): Promise<ThreeCXForceUpdateResult> {
    const response = await this.post<ThreeCXForceUpdateResult>(
      "/business/3cx/force-update/opening-hours",
      { companyId }
    );
    return response.data!;
  }

  /**
   * Force update 3CX holidays (Admin only)
   */
  async forceUpdate3CXHolidays(
    companyId?: string
  ): Promise<ThreeCXForceUpdateResult> {
    const response = await this.post<ThreeCXForceUpdateResult>(
      "/business/3cx/force-update/holidays",
      { companyId }
    );
    return response.data!;
  }

  /**
   * Force update all 3CX data (Admin only)
   */
  async forceUpdate3CXAll(companyId?: string): Promise<{
    openingHours: ThreeCXForceUpdateResult;
    holidays: ThreeCXForceUpdateResult;
    summary: string;
  }> {
    const response = await this.post<{
      openingHours: ThreeCXForceUpdateResult;
      holidays: ThreeCXForceUpdateResult;
      summary: string;
    }>("/business/3cx/force-update/all", { companyId });
    return response.data!;
  }

  /**
   * Add holiday with 3CX sync
   */
  async addHolidayWith3CX(request: CreateHolidayRequest): Promise<Holiday> {
    const response = await this.post<Holiday>(
      "/business/holidays/with-3cx",
      request
    );
    return response.data!;
  }

  /**
   * Get all holidays (including inactive ones)
   */
  async getHolidays(includeInactive: boolean = true): Promise<Holiday[]> {
    const params = includeInactive ? { includeInactive: "true" } : undefined;
    const response = await this.get<Holiday[]>("/business/holidays", params);
    return response.data!;
  }

  /**
   * Delete holiday (automatically handles deletion vs deactivation)
   */
  async deleteHoliday(holidayId: string): Promise<{
    action: "deleted" | "deactivated";
    holiday?: Holiday;
  }> {
    const response = await this.delete<any>(`/business/holidays/${holidayId}`);

    // Handle different response structures from backend
    if (response.data && typeof response.data === "object") {
      // If backend returns nested data structure
      return response.data.data || response.data;
    }

    // If backend returns direct structure
    return response as any;
  }

  /**
   * Activate holiday (with conflict checking)
   */
  async activateHoliday(holidayId: string): Promise<{
    success: boolean;
    error?: string;
    conflicts?: Holiday[];
    holiday?: Holiday;
  }> {
    const response = await this.post<any>(
      `/business/holidays/${holidayId}/activate`
    );

    // Handle different response structures from backend
    if (response.data && typeof response.data === "object") {
      // If backend returns nested data structure
      return response.data.data || response.data;
    }

    // If backend returns direct structure
    return response as any;
  }

  /**
   * Deactivate holiday
   */
  async deactivateHoliday(holidayId: string): Promise<{
    success: boolean;
    error?: string;
    holiday?: Holiday;
  }> {
    const response = await this.post<any>(
      `/business/holidays/${holidayId}/deactivate`
    );

    // Handle different response structures from backend
    if (response.data && typeof response.data === "object") {
      // If backend returns nested data structure
      return response.data.data || response.data;
    }

    // If backend returns direct structure
    return response as any;
  }

  /**
   * Remove holiday with 3CX sync
   */
  async removeHolidayWith3CX(holidayId: string): Promise<void> {
    await this.delete(`/business/holidays/${holidayId}/with-3cx`);
  }

  /**
   * Set opening hours with 3CX sync
   */
  async setOpeningHoursWith3CX(
    request: SetOpeningHoursRequest
  ): Promise<OpeningHours> {
    const response = await this.post<OpeningHours>(
      "/business/opening-hours/with-3cx",
      request
    );
    return response.data!;
  }

  /**
   * Set business status with 3CX sync
   */
  async setBusinessStatusWith3CX(
    request: SetBusinessStatusRequest
  ): Promise<BusinessStatus> {
    const response = await this.post<BusinessStatus>(
      "/business/status/with-3cx",
      request
    );
    return response.data!;
  }

  // ============================================
  // TELEFON-OFFSET METHODS
  // ============================================

  /**
   * Get phone offsets
   */
  async getPhoneOffsets(): Promise<PhoneOffsets> {
    const response = await this.get<PhoneOffsets>("/business/phone-offsets");
    return response.data!;
  }

  /**
   * Set phone offsets
   */
  async setPhoneOffsets(
    request: SetPhoneOffsetsRequest
  ): Promise<SetPhoneOffsetsResponse> {
    const response = await this.post<SetPhoneOffsetsResponse>(
      "/business/phone-offsets",
      request
    );
    return response.data!;
  }

  /**
   * Get default business status within opening hours (OPEN or REQUEST)
   */
  async getDefaultOpenStatus(): Promise<{ status: BusinessStatusMode }> {
    const response = await this.get<{ status: BusinessStatusMode }>(
      "/business/default-open-status"
    );
    return response.data!;
  }

  /**
   * Set default business status within opening hours (OPEN or REQUEST)
   */
  async setDefaultOpenStatus(
    status: BusinessStatusMode
  ): Promise<{ status: BusinessStatusMode }> {
    const response = await this.post<{ status: BusinessStatusMode }>(
      "/business/default-open-status",
      { status }
    );
    return response.data!;
  }

  async getAiSettings(): Promise<AiSettings> {
    const response = await this.get<AiSettings>("/chat/ai-settings");
    return response.data!;
  }

  async updateAiSettings(request: Partial<AiSettings>): Promise<AiSettings> {
    const response = await this.put<AiSettings>("/chat/ai-settings", request);
    return response.data!;
  }

  async runLiftOccupancyDryRun(options: { applyInternalBlockers?: boolean } = {}): Promise<LiftOccupancyDryRunResult> {
    const response = await this.post<LiftOccupancyDryRunResult>(
      "/chat/ai-settings/lift-occupancy-dry-run",
      options
    );
    return response.data!;
  }

  async getPresenceCameraConfig(): Promise<PresenceCameraConfig> {
    const response = await this.get<PresenceCameraConfig>(
      "/chat/presence-camera"
    );
    return response.data!;
  }

  async updatePresenceCameraConfig(
    request: PresenceCameraConfig
  ): Promise<PresenceCameraConfig> {
    const response = await this.put<PresenceCameraConfig>(
      "/chat/presence-camera",
      request
    );
    return response.data!;
  }

  async testPresenceCameraConfig(
    request?: Partial<PresenceCameraConfig>
  ): Promise<PresenceCameraVerdict> {
    const response = await this.post<PresenceCameraVerdict>(
      "/chat/presence-camera/test",
      request ?? {}
    );
    return response.data!;
  }

  async checkPresenceObjects(
    request?: Partial<PresenceCameraConfig>
  ): Promise<PresenceObjectCheckResult> {
    const response = await this.post<PresenceObjectCheckResult>(
      "/chat/presence-camera/object-check",
      request ?? {}
    );
    return response.data!;
  }

  async getPresenceCameraSnapshot(
    cameraId: string,
    config?: Partial<PresenceCameraConfig>
  ): Promise<{ image: string }> {
    const response = await this.post<{ image: string }>(
      "/chat/presence-camera/snapshot",
      { cameraId, config }
    );
    return response.data!;
  }
}
